import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Table, Button } from "antd";
import { useAuth } from "../context/AuthContext";
import Sidebar from "./Sidebar";
import "./styles/Analytics.css";
import countryList from "react-select-country-list";
// PHASE 2B: Import unified analytics hook
// ⚠️ CRITICAL: Uses /api/analytics endpoint (NOT /api/email-campaigns)
// This endpoint properly calculates firstSent/lastSent from sendLog subcollection
import { useAnalytics } from "../hooks/useCampaignData";

// ✅ OPTIMIZATION: Country mapping with O(1) Map lookup (instead of O(N) array.find)
const allCountries = countryList().getData();
// ✅ FIX: "Global" should be FIRST OPTION + placeholder (matches MEO exactly)
const countryOptions = [
  { name: "Global", code: "" }, // ✅ First option in list (matches MEO)
  ...allCountries.map((country) => ({
    name: country.label,
    code: country.value,
  }))
];

// Create Map for O(1) country lookups (computed once at module load)
const countryNameMap = new Map([['', 'Global']]);
allCountries.forEach(country => {
  countryNameMap.set(country.value, country.label);
});

// Helper function to get full country name from country code (O(1) lookup)
const getCountryName = (code) => {
  return countryNameMap.get(code) || code;
};

// Component for New Daily Quota - unified quota tracking system
const NewDailyQuota = ({ dailyEmailsSent, gmailAccount }) => {
  console.log("🔍 NewDailyQuota received props:", { dailyEmailsSent, gmailAccount });
  
  // ✅ ENHANCED VALIDATION: Check data types and values
  const emailsSentToday = typeof dailyEmailsSent === 'number' ? dailyEmailsSent : 0;
  const maxQuota = 40;
  const remaining = Math.max(0, maxQuota - emailsSentToday);
  const percentage = Math.round((emailsSentToday / maxQuota) * 100);
  
  console.log("🔍 NewDailyQuota calculations:", {
    emailsSentToday,
    maxQuota,
    remaining,
    percentage: `${percentage}%`,
    isValidData: typeof dailyEmailsSent === 'number'
  });

  let quotaColor = "#52c41a";
  if (percentage >= 80) quotaColor = "#faad14";
  if (percentage >= 100) quotaColor = "#ff4d4f";

  return (
    <div style={{ minHeight: '40px', padding: '2px' }}>
      <div style={{ color: quotaColor, fontWeight: "bold" }}>
        {emailsSentToday} / {maxQuota}
      </div>
      <div style={{ fontSize: "11px", color: "#666" }}>
        {remaining} remaining today
      </div>
      {gmailAccount && gmailAccount !== 'N/A' && (
        <div style={{ fontSize: "10px", color: "#999" }}>
          {gmailAccount}
        </div>
      )}
      {(!dailyEmailsSent && dailyEmailsSent !== 0) && (
        <div style={{ fontSize: "10px", color: "#ff4d4f" }}>
          No quota data
        </div>
      )}
      {(dailyEmailsSent === 0) && (
        <div style={{ fontSize: "10px", color: "#52c41a" }}>
          ✅ Zero emails sent today
        </div>
      )}
    </div>
  );
};

// Component for Progress cell - uses backend-calculated data
const ProgressInfo = ({ totalSent, totalRecipients }) => {
  const sent = totalSent || 0;
  const total = totalRecipients || 0;
  const progressPercent = total > 0 ? Math.round((sent / total) * 100) : 0;
  
  return (
    <div>
      <div style={{ color: '#52c41a', fontSize: '14px', fontWeight: '500' }}>
        {sent} / {total}
      </div>
      <div style={{ fontSize: '11px', color: '#666' }}>
        {progressPercent}% complete
      </div>
      <div style={{ color: '#52c41a', fontSize: '12px' }}>✅</div>
    </div>
  );
};

// ✅ OPTIMIZATION: Summary component for instant first-content display
const AnalyticsSummary = ({ stats }) => {
  if (!stats) return null;
  
  const progressPercent = stats.totalRecipients > 0 
    ? Math.round((stats.totalSent / stats.totalRecipients) * 100) 
    : 0;
  
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px',
      padding: '16px',
      background: '#f9f9f9',
      borderRadius: '8px'
    }}>
      <div style={{ padding: '16px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Campaigns</div>
        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff' }}>{stats.totalCampaigns}</div>
      </div>
      
      <div style={{ padding: '16px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Active Campaigns</div>
        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#52c41a' }}>{stats.activeCampaigns}</div>
        {stats.pausedCampaigns > 0 && (
          <div style={{ fontSize: '12px', color: '#faad14', marginTop: '4px' }}>
            ({stats.pausedCampaigns} paused)
          </div>
        )}
      </div>
      
      <div style={{ padding: '16px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Emails Sent</div>
        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#722ed1' }}>{stats.totalSent.toLocaleString()}</div>
        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
          {progressPercent}% of {stats.totalRecipients.toLocaleString()} recipients
        </div>
      </div>
      
      <div style={{ padding: '16px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Completed</div>
        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#52c41a' }}>{stats.completedCampaigns}</div>
      </div>
    </div>
  );
};

const Analytics = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ✅ OPTIMIZATION: Lazy loading state for progressive UI
  const [showTable, setShowTable] = useState(false);
  const [summaryStats, setSummaryStats] = useState(null);
  const { user } = useAuth();

  // PHASE 2B: Use unified analytics hook for instant analytics loads
  // ⚠️ CRITICAL: This uses /api/analytics (which properly calculates firstSent/lastSent from sendLog)
  // NOT /api/email-campaigns (which doesn't have sendLog aggregation logic)
  const { 
    data: cachedAnalytics, 
    isLoading: analyticsLoading, 
    error: analyticsError 
  } = useAnalytics(user?.uid, { enabled: !!user });

  // ✅ PERFORMANCE: Update campaigns state when cached data changes
  // ✅ SORTING: Sort by Last Sent descending (newest first, nulls last)
  useEffect(() => {
    if (cachedAnalytics?.campaigns) {
      const campaigns = Array.isArray(cachedAnalytics.campaigns) ? cachedAnalytics.campaigns : [];
      
      // ✅ OPTIMIZATION: Backend already sorts by lastSent (analytics.js:191-196)
      // No need to re-sort on frontend - saves 50-100ms
      setCampaigns(campaigns);
      console.log(`📦 [Analytics] Loaded ${campaigns.length} campaigns (pre-sorted by backend)`);
      
      // ✅ OPTIMIZATION: Calculate summary stats immediately for instant display
      const stats = {
        totalCampaigns: campaigns.length,
        activeCampaigns: campaigns.filter(c => c.status === 'running' && !c.paused).length,
        pausedCampaigns: campaigns.filter(c => c.status === 'running' && c.paused).length,
        completedCampaigns: campaigns.filter(c => c.status === 'completed').length,
        totalSent: campaigns.reduce((sum, c) => sum + (c.sentCount || 0), 0),
        totalRecipients: campaigns.reduce((sum, c) => sum + (c.totalRecipients || 0), 0)
      };
      setSummaryStats(stats);
      console.log(`📊 [Analytics] Summary stats calculated:`, stats);
      
      setLoading(false);
      setError(null);
      
      // ✅ OPTIMIZATION: Delay table render by 100ms to show summary first
      setTimeout(() => {
        setShowTable(true);
        console.log(`📋 [Analytics] Table rendered (lazy loaded after summary)`);
      }, 100);
      
      console.log(`📦 [Cache] Analytics loaded ${campaigns.length} campaigns from cache`);
      
      // ✅ VERIFICATION: Log first campaign's date fields to confirm preservation
      if (campaigns.length > 0) {
        const firstCampaign = campaigns[0];
        console.log("🔍 First campaign data verification:", {
          id: firstCampaign.id,
          name: firstCampaign.name,
          firstSent: firstCampaign.firstSent,
          lastSent: firstCampaign.lastSent,
          firstSentType: typeof firstCampaign.firstSent,
          lastSentType: typeof firstCampaign.lastSent,
          dailyEmailsSent: firstCampaign.dailyEmailsSent,
          gmailAccount: firstCampaign.gmailAccount
        });
        
        // ⚠️ SAFETY: Verify dates are still ISO strings
        if (firstCampaign.firstSent && typeof firstCampaign.firstSent !== 'string') {
          console.error('❌ CRITICAL: firstSent is not a string!', typeof firstCampaign.firstSent);
        }
        if (firstCampaign.lastSent && typeof firstCampaign.lastSent !== 'string') {
          console.error('❌ CRITICAL: lastSent is not a string!', typeof firstCampaign.lastSent);
        }
      }
    }
  }, [cachedAnalytics]);
  
  // ✅ PERFORMANCE: Update loading state from React Query
  useEffect(() => {
    setLoading(analyticsLoading);
  }, [analyticsLoading]);
  
  // ✅ Handle analytics errors
  useEffect(() => {
    if (analyticsError) {
      console.error("❌ Analytics: Error fetching analytics:", analyticsError);
      
      let errorMessage = "Failed to load analytics";
      
      if (analyticsError.response) {
        const status = analyticsError.response.status;
        const data = analyticsError.response.data;
        
        if (status === 401) {
          errorMessage = "Authentication failed. Please log in again.";
        } else if (status === 403) {
          errorMessage = "Access denied. You don't have permission to view analytics.";
        } else if (status === 404) {
          errorMessage = "Analytics endpoint not found. Please check your API configuration.";
        } else if (status === 500) {
          errorMessage = `Server error: ${data?.error || 'Please try again later'}`;
        } else {
          errorMessage = `Error (${status}): ${data?.error || 'Unknown error'}`;
        }
      } else if (analyticsError.request) {
        errorMessage = "Network error. Please check if the server is running.";
      } else {
        errorMessage = analyticsError.message || "An unexpected error occurred";
      }
      
      setError(errorMessage);
      setCampaigns([]);
      setLoading(false);
    }
  }, [analyticsError]);

  // ⚠️ PRESERVATION: Helper to format date with time - UNCHANGED
  // This function receives ISO strings from backend and formats them
  // Exact same logic as before caching - no modifications
  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // ✅ SORTABLE: All columns have sorting enabled (ascending/descending toggle)
  // Column order: Search Term | Country | First Sent | Last Sent | Progress | New Daily Quota | Status | Email Mailbox Used
  const columns = [
    {
      title: 'Search Term',
      dataIndex: 'searchTerm',
      key: 'searchTerm',
      width: 200,
      ellipsis: true,
      sorter: (a, b) => {
        const aText = a.searchTerm || a.hashtag || '';
        const bText = b.searchTerm || b.hashtag || '';
        return aText.localeCompare(bText);
      },
      render: (text, record) => <span style={{ fontWeight: 500 }}>{text || record.hashtag || '—'}</span>
    },
    {
      title: 'Country',
      dataIndex: 'targetCountry',
      key: 'targetCountry',
      width: 150,
      sorter: (a, b) => {
        const aCountry = getCountryName(a.targetCountry);
        const bCountry = getCountryName(b.targetCountry);
        return aCountry.localeCompare(bCountry);
      },
      render: (country) => (
        <span style={{ fontWeight: 500, color: '#000' }}>{getCountryName(country)}</span>
      )
    },
    {
      title: 'First Sent',
      dataIndex: 'firstSent',
      key: 'firstSent',
      width: 180,
      sorter: (a, b) => {
        if (!a.firstSent && !b.firstSent) return 0;
        if (!a.firstSent) return 1;
        if (!b.firstSent) return -1;
        return new Date(a.firstSent) - new Date(b.firstSent);
      },
      render: (text) => formatDateTime(text)
    },
    {
      title: 'Last Sent',
      dataIndex: 'lastSent',
      key: 'lastSent',
      width: 180,
      sorter: (a, b) => {
        if (!a.lastSent && !b.lastSent) return 0;
        if (!a.lastSent) return 1;
        if (!b.lastSent) return -1;
        return new Date(a.lastSent) - new Date(b.lastSent);
      },
      render: (text, record) => {
        const dateStr = formatDateTime(text);
        if (!text) return dateStr;
        
        // Add relative time
        const lastSent = new Date(text);
        const now = new Date();
        const diffMs = now - lastSent;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        
        if (diffHours < 24) {
          return (
            <div>
              <div>{dateStr}</div>
              <div style={{ fontSize: '11px', color: '#666' }}>
                ({diffHours}h ago)
              </div>
            </div>
          );
        }
        
        return dateStr;
      }
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 150,
      sorter: (a, b) => {
        const aSent = a.sentCount || a.totalSent || 0;
        const bSent = b.sentCount || b.totalSent || 0;
        const aTotal = a.totalRecipients || 1;
        const bTotal = b.totalRecipients || 1;
        const aPercent = (aSent / aTotal) * 100;
        const bPercent = (bSent / bTotal) * 100;
        return aPercent - bPercent;
      },
      render: (_, record) => (
        <ProgressInfo 
          totalSent={record.sentCount || record.totalSent || 0} 
          totalRecipients={record.totalRecipients || 0} 
        />
      )
    },
    {
      title: 'New Daily Quota',
      key: 'dailyQuota',
      width: 180,
      sorter: (a, b) => {
        const aQuota = a.dailyEmailsSent || 0;
        const bQuota = b.dailyEmailsSent || 0;
        return aQuota - bQuota;
      },
      render: (_, record) => (
        <NewDailyQuota 
          dailyEmailsSent={record.dailyEmailsSent} 
          gmailAccount={record.gmailAccount} 
        />
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: (a, b) => {
        const statusOrder = {
          'running': 1,
          'paused': 2,
          'pending': 3,
          'completed': 4,
          'draft': 5
        };
        const aStatus = a.paused ? 'paused' : (a.status || 'draft');
        const bStatus = b.paused ? 'paused' : (b.status || 'draft');
        return (statusOrder[aStatus] || 99) - (statusOrder[bStatus] || 99);
      },
      render: (status, record) => {
        if (status === "running" && record.paused) {
          return <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Paused</span>;
        } else if (status === "running" && !record.paused) {
          return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>Running</span>;
        } else if (status === "completed") {
          return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>Completed</span>;
        } else if (status === "pending") {
          const recipientCount = record.totalRecipients || 0;
          if (recipientCount > 0) {
            return <span style={{ color: '#1890ff', fontWeight: 'bold' }}>Pending</span>;
          } else {
            return <span style={{ color: '#faad14', fontWeight: 'bold' }}>Awaiting Recipients</span>;
          }
        } else if (status === "draft") {
          return <span style={{ color: '#faad14', fontWeight: 'bold' }}>Draft</span>;
        }
        return <span style={{ fontWeight: 'bold' }}>{status || 'Unknown'}</span>;
      }
    },
    {
      title: 'Email Mailbox Used',
      dataIndex: 'gmailAccount',
      key: 'gmailAccount',
      width: 200,
      sorter: (a, b) => {
        const aEmail = a.gmailAccount || '';
        const bEmail = b.gmailAccount || '';
        return aEmail.localeCompare(bEmail);
      },
      render: (gmailAccount) => gmailAccount || '—'
    }
  ];

  return (
    <div className="analytics-layout">
      <Sidebar />
      <div className="analytics-main">
        <div className="analytics-header">
          <h2>Campaign Analytics</h2>
        </div>

        {error && (
          <div className="error-message">
            <span>⚠️ {error}</span>
            {/* ✅ React Query will automatically retry - but keeping retry button for user convenience */}
            <Button onClick={() => window.location.reload()} type="primary">
              Retry
            </Button>
          </div>
        )}

        {loading ? (
          <div className="analytics-loading">
            <div className="loading-spinner"></div>
            <p>Loading analytics data...</p>
          </div>
        ) : (
          <>
            {/* ✅ HIDDEN: Summary cards kept in code but not displayed in UI */}
            {false && summaryStats && <AnalyticsSummary stats={summaryStats} />}
            
            {/* ✅ OPTIMIZATION: Lazy load table after 100ms */}
            {showTable ? (
              <div className="analytics-table">
                <Table
                  columns={columns}
                  dataSource={campaigns}
                  rowKey="id"
                  pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} campaigns`,
                  }}
                  scroll={{ x: 'max-content' }}
                  locale={{
                    emptyText: campaigns.length === 0 && !error ? 
                      "No campaigns found. Create your first campaign to see analytics here!" : 
                      "No campaigns found"
                  }}
                />
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <div className="loading-spinner"></div>
                <p>Loading campaign details...</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;
